import z from "zod";

import { CheckoutApplicationFieldType } from "@/models/options";

import type { IOffer } from "@/models/funders";

const checkoutStepTypeDocumentsSchema = z.literal("documents");
const checkoutStepTypeConsentsSchema = z.literal("consents");
const checkoutStepTypeContractsSchema = z.literal("contracts");
const checkoutStepTypeDataSchema = z.literal("data");
const checkoutStepTypeServiceSchema = z.literal("service");
const checkoutStepTypeCustomSchema = z.literal("custom");

export const checkoutStepTypeSchema = z.union([
  checkoutStepTypeDocumentsSchema,
  checkoutStepTypeConsentsSchema,
  checkoutStepTypeContractsSchema,
  checkoutStepTypeDataSchema,
  checkoutStepTypeServiceSchema,
  checkoutStepTypeCustomSchema,
]);

export interface ServicePreviewContent {
  title: string;
  description: string;
  buttonText: string;
}

const checkoutDataFieldSchema = z.object({
  id: z.string(),
  type: z.enum(["application_field", "custom_field"]),
  name: z.string(),
  label: z.string(),
  value_type: z.enum(CheckoutApplicationFieldType).optional(),
  block: z.string().optional(),
});

const checkoutDataTableSchema = z.object({
  id: z.string(),
  type: z.enum(["table"]),
  layout: z.enum(["table", "form"]),
  fields: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      label: z.string(),
    })
  ),
  block: z.string(),
  title: z.string().optional(),
});

const checkoutDataStepItemSchema = z.discriminatedUnion("type", [
  checkoutDataFieldSchema,
  checkoutDataTableSchema,
]);

const checkoutStepBaseSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
});

const checkoutDocumentsStepSchema = checkoutStepBaseSchema.extend({
  type: checkoutStepTypeDocumentsSchema,
});

const checkoutConsentsStepSchema = checkoutStepBaseSchema.extend({
  type: checkoutStepTypeConsentsSchema,
  templates: z.array(
    z.object({
      id: z.union([z.string(), z.number()]),
      owners_required: z.string().optional(),
    })
  ),
});

const checkoutContractsStepSchema = checkoutStepBaseSchema.extend({
  type: checkoutStepTypeContractsSchema,
  templates: z.array(
    z.object({
      id: z.union([z.string(), z.number()]),
      owners_required: z.string().optional(),
    })
  ),
});

const checkoutDataStepSchema = checkoutStepBaseSchema.extend({
  type: checkoutStepTypeDataSchema,
  items: z.array(checkoutDataStepItemSchema),
});

const checkoutServiceStepSchema = checkoutStepBaseSchema.extend({
  type: checkoutStepTypeServiceSchema,
  service_id: z.string(),
});

const checkoutCustomStepSchema = checkoutStepBaseSchema.extend({
  type: checkoutStepTypeCustomSchema,
  custom_title: z.string().nullish(),
  custom_description: z.string().nullish(),
  custom_button_text: z.string().nullish(),
  custom_button_url: z.string().nullish(),
});

const checkoutTemplateStepSchema = z.discriminatedUnion("type", [
  checkoutDocumentsStepSchema,
  checkoutConsentsStepSchema,
  checkoutContractsStepSchema,
  checkoutDataStepSchema,
  checkoutServiceStepSchema,
  checkoutCustomStepSchema,
]);

export const checkoutTemplateBaseSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  client: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .nullable()
    .optional(),
  data_step_values: z.record(z.string(), z.unknown()).optional(),
});

const checkoutTemplateContentSchema = z.object({
  id: z.string().optional(),
  version: z.string().optional(),
  title: z.string(),
  subtitle: z.string().nullable(),
  checkout_section_name: z.string().nullable().optional(),
  offer_summary_section_name: z.string().nullable().optional(),
  should_mask_piis: z.boolean().optional(),
  steps: z.array(checkoutTemplateStepSchema),
  finalize: z.object({
    title: z.string().nullable(),
    description: z.string().nullable(),
  }),
});

export const checkoutTemplateSchema = checkoutTemplateBaseSchema.extend({
  content: checkoutTemplateContentSchema,
});

export type CheckoutTemplateBase = z.infer<typeof checkoutTemplateBaseSchema>;
export type CheckoutTemplateContent = z.infer<
  typeof checkoutTemplateContentSchema
>;
export type CheckoutTemplate = z.infer<typeof checkoutTemplateSchema>;

export type CheckoutStep = z.infer<typeof checkoutTemplateStepSchema>;
export type CheckoutStepType = z.infer<typeof checkoutStepTypeSchema>;

export type CheckoutDataField = z.infer<typeof checkoutDataFieldSchema>;
export type CheckoutDataTable = z.infer<typeof checkoutDataTableSchema>;
export type CheckoutDataStepItem = z.infer<typeof checkoutDataStepItemSchema>;

export type CheckoutDocumentsStep = z.infer<typeof checkoutDocumentsStepSchema>;
export type CheckoutConsentsStep = z.infer<typeof checkoutConsentsStepSchema>;
export type CheckoutContractsStep = z.infer<typeof checkoutContractsStepSchema>;
export type CheckoutDataStep = z.infer<typeof checkoutDataStepSchema>;
export type CheckoutServiceStep = z.infer<typeof checkoutServiceStepSchema>;
export type CheckoutCustomStep = z.infer<typeof checkoutCustomStepSchema>;

export type CheckoutSnapshot = Omit<CheckoutTemplate, "id"> & {
  id: number;
  offer_id: IOffer["id"];
  checkout_template_id: NonNullable<CheckoutTemplateBase["id"]>;
};

export type UpdateCheckoutSnapshotParams = {
  offerId: string;
  checkoutSnapshotId: string;
  checkoutContent: CheckoutTemplateContent;
};

export type CheckoutBuilderMode = "in-progress" | "complete";

export type CheckoutProgress = {
  id: number;
  checkout_snapshot_id: number;
  status: "complete" | "incomplete";
  step_statuses: {
    id: string;
    is_complete: boolean;
  }[];
  started_at: string;
  completed_at: string;
  created_at: string;
  updated_at: string;
};
